import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShowcaseShell } from '../components/showcase/ShowcaseShell';
import { ShowcaseNowPanes, type ShowcaseBoothPicture } from '../components/showcase/ShowcaseNowPanes';
import { ShowcaseBadge } from '../components/showcase/ShowcaseShell';
import {
  MIS_STICKY_ANNOTATED_CAMERA_ID,
  MIS_STICKY_ANNOTATED_DEVICE_ID,
  useSpatialOperationsLiveCamera,
} from '../hooks/useSpatialOperationsLiveCamera';
import { useShowcaseLiveNow } from '../hooks/useShowcaseLiveNow';
import { useShowcaseSpatialZoneOverlay } from '../hooks/useShowcaseSpatialZoneOverlay';
import { ensureUiScope } from '../services/api';
import { parseSpatialOperationsSearch } from '../domain/spatialOperations/spatialOperationsUrl';
import { useShowcaseConditionData } from '../hooks/useShowcaseConditionData';
import { useShowcaseRecentEvidence } from '../hooks/useShowcaseRecentEvidence';
import { formatSiteTime } from '../domain/siteTime/siteTime';
import { showcaseCustomerTimeZone } from '../domain/showcase/showcaseCustomerParity';
import { useSiteTimeZone } from '../hooks/useSiteTimeZone';
import {
  presentShowcaseRecentEvidence,
  SHOWCASE_PROX_EVIDENCE_CAMERA_ID,
  SHOWCASE_PROX_EVIDENCE_INCIDENT_TYPE,
  SHOWCASE_RAE_EVIDENCE_CAMERA_ID,
  SHOWCASE_RAE_EVIDENCE_INCIDENT_TYPE,
  SHOWCASE_RAE_EVIDENCE_ZONE_ID,
} from '../domain/showcase/showcaseRecentEvidence';
import {
  applyShowcaseKey,
  buildShowcasePath,
  clearShowcaseSession,
  isCustomerShowcasePath,
  nowStreamsActive,
  parseShowcaseSearch,
  presentationResetState,
  resolveShowcasePaneOnLoad,
  shouldForceCustomerLiveBooth,
  showcaseVisitorPane,
  stripRecordedShowcaseParams,
  writeShowcaseCustomerRoute,
  writeShowcaseSession,
  SHOWCASE_LABEL_DEMO_DATASET,
  SHOWCASE_LABEL_FALLBACK,
  SHOWCASE_ROOM_B_PROXIMITY_CONDITION_ID,
  SHOWCASE_ROOM_B_RAE_CONDITION_ID,
  type ShowcasePane,
  type ShowcaseRoomBCondition,
  type ShowcaseSource,
} from '../domain/showcase/showcasePresentation';
import { SHOWCASE_PPE_STILL } from '../domain/showcase/showcaseStills';
import { createFailOpenUsbLightAdapter } from '../domain/showcase/usbLightAdapter';

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export default function ShowcasePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const parsed = useMemo(
    () => resolveShowcasePaneOnLoad(
      parseShowcaseSearch(location.search, location.hash),
      location.search,
      location.hash,
      location.pathname,
    ),
    [location.hash, location.pathname, location.search],
  );
  const [pane, setPane] = useState<ShowcasePane>(parsed.pane);
  const [fallback, setFallback] = useState(parsed.fallback);
  const [source, setSource] = useState<ShowcaseSource>(parsed.source);
  const [roomBCondition, setRoomBCondition] = useState<ShowcaseRoomBCondition>(parsed.roomBCondition);
  const [lampMessage, setLampMessage] = useState<string | null>(null);
  const [healthOpen, setHealthOpen] = useState(false);
  const [resetNonce, setResetNonce] = useState(0);
  const [boothPicture, setBoothPicture] = useState<ShowcaseBoothPicture>('annotated');
  const [startedAt] = useState(() => Date.now());
  const adapter = useMemo(() => createFailOpenUsbLightAdapter({ connected: false }), []);
  const siteId = useMemo(
    () => parseSpatialOperationsSearch(location.search).siteId || ensureUiScope().siteId || null,
    [location.search],
  );
  const siteTimeZone = showcaseCustomerTimeZone(useSiteTimeZone(siteId));
  const liveConfig = useMemo(() => ({
    siteId,
    cameraId: parsed.boothCameraId,
    zoneId: parsed.boothZoneId,
    policyId: parsed.boothPolicyId,
    deviceId: parsed.boothDeviceId,
  }), [parsed.boothCameraId, parsed.boothDeviceId, parsed.boothPolicyId, parsed.boothZoneId, siteId]);

  useEffect(() => {
    setPane(parsed.pane);
    setFallback(parsed.fallback);
    setSource(parsed.source);
    setRoomBCondition(parsed.roomBCondition);
  }, [parsed.fallback, parsed.pane, parsed.roomBCondition, parsed.source]);

  useEffect(() => {
    if (!isCustomerShowcasePath(location.pathname)) return;
    if (shouldForceCustomerLiveBooth(location.pathname, location.search, location.hash)) {
      writeShowcaseSession(presentationResetState());
      const nextSearch = stripRecordedShowcaseParams(location.search);
      if (nextSearch !== location.search) {
        navigate({ pathname: location.pathname, search: nextSearch, hash: location.hash }, { replace: true });
      }
    }
    writeShowcaseCustomerRoute(location.pathname);
  }, [location.hash, location.pathname, location.search, navigate]);

  const commit = useCallback((nextPane: ShowcasePane, nextFallback: boolean, nextSource = source, nextCondition = roomBCondition) => {
    setPane(nextPane);
    setFallback(nextFallback);
    setSource(nextSource);
    setRoomBCondition(nextCondition);
    writeShowcaseSession({
      pane: nextPane,
      fallback: nextFallback,
      source: nextSource,
      roomBCondition: nextCondition,
    });
    const path = buildShowcasePath({
      pane: nextPane,
      fallback: nextFallback,
      source: nextSource,
      roomBCondition: nextCondition,
      currentSearch: location.search,
    });
    if (`${location.pathname}${location.search}` !== path) {
      navigate(path, { replace: true });
    }
  }, [location.pathname, location.search, navigate, roomBCondition, source]);

  const visitorPane = showcaseVisitorPane(pane, source);
  const visitorSource = visitorPane === 'forklift' ? 'room_b' : 'booth';
  const streamsOn = nowStreamsActive(visitorPane, fallback);
  const misStickyAnnotated = (
    parsed.boothDeviceId === MIS_STICKY_ANNOTATED_DEVICE_ID
    && parsed.boothCameraId === MIS_STICKY_ANNOTATED_CAMERA_ID
  );
  const booth = useSpatialOperationsLiveCamera({
    active: streamsOn && Boolean(parsed.boothCameraId),
    cameraId: parsed.boothCameraId,
    edgeDeviceId: parsed.boothDeviceId,
    cameraOnline: parsed.boothCameraId ? true : null,
    snapshotUrl: null,
    viewKind: boothPicture === 'original' ? 'live' : 'annotated',
    stickyAnnotated: misStickyAnnotated,
  });
  const forklift = useSpatialOperationsLiveCamera({
    active: streamsOn && Boolean(parsed.forkliftCameraId),
    cameraId: parsed.forkliftCameraId,
    edgeDeviceId: parsed.forkliftDeviceId,
    cameraOnline: parsed.forkliftCameraId ? true : null,
    snapshotUrl: null,
  });
  const reconnectForklift = forklift.reconnect;
  const liveNow = useShowcaseLiveNow({
    active: !fallback,
    config: liveConfig,
    resetNonce,
  });
  const showRoomBRaeOverlay = visitorPane === 'forklift';
  const forkliftZone = useShowcaseSpatialZoneOverlay({
    active: !fallback && showRoomBRaeOverlay && Boolean(parsed.forkliftCameraId),
    siteId,
    cameraId: parsed.forkliftCameraId,
    conditionId: parsed.forkliftConditionId || SHOWCASE_ROOM_B_RAE_CONDITION_ID,
    zoneId: parsed.forkliftZoneId,
    excludeZoneId: parsed.boothZoneId,
  });
  const conditionData = useShowcaseConditionData({
    active: Boolean(siteId),
    siteId,
    source: visitorSource,
    roomBCondition,
    boothCameraId: parsed.boothCameraId,
    roomBCameraId: parsed.forkliftCameraId,
    roomBRaeConditionId: parsed.forkliftConditionId || SHOWCASE_ROOM_B_RAE_CONDITION_ID,
    roomBProximityConditionId: parsed.roomBProximityConditionId || SHOWCASE_ROOM_B_PROXIMITY_CONDITION_ID,
    timeZone: siteTimeZone,
  });
  const raePeak = conditionData.bundle?.roomBRae?.recurring?.peak;
  const proximityPeak = conditionData.bundle?.roomBProximity?.recurring?.peak;
  const recentRae = useShowcaseRecentEvidence({
    enabled: visitorPane === 'forklift' && Boolean(siteId),
    siteId,
    cameraId: parsed.forkliftCameraId || SHOWCASE_RAE_EVIDENCE_CAMERA_ID,
    conditionId: parsed.forkliftConditionId || SHOWCASE_ROOM_B_RAE_CONDITION_ID,
    zoneId: parsed.forkliftZoneId || SHOWCASE_RAE_EVIDENCE_ZONE_ID,
    incidentType: SHOWCASE_RAE_EVIDENCE_INCIDENT_TYPE,
    occurredFrom: raePeak?.start,
    occurredTo: raePeak?.end,
  });
  const recentProximity = useShowcaseRecentEvidence({
    enabled: visitorPane === 'forklift' && Boolean(siteId),
    siteId,
    cameraId: parsed.forkliftCameraId || SHOWCASE_PROX_EVIDENCE_CAMERA_ID,
    conditionId: parsed.roomBProximityConditionId || SHOWCASE_ROOM_B_PROXIMITY_CONDITION_ID,
    incidentType: SHOWCASE_PROX_EVIDENCE_INCIDENT_TYPE,
    occurredFrom: proximityPeak?.start,
    occurredTo: proximityPeak?.end,
  });
  const presentEvidence = (state: typeof recentRae) => ({
    status: state.status,
    items: presentShowcaseRecentEvidence(
      state.items,
      (timestamp) => formatSiteTime(timestamp, siteTimeZone),
    ),
  });
  const raeEvidence = presentEvidence(recentRae);
  const proximityEvidence = presentEvidence(recentProximity);

  useEffect(() => {
    if (visitorPane !== 'forklift' || fallback) return;
    reconnectForklift?.();
  }, [visitorPane, fallback, reconnectForklift]);

  useEffect(() => {
    void adapter.applyPhase(liveNow.phase);
  }, [adapter, liveNow.phase]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      if (event.key.toLowerCase() === 'h') {
        event.preventDefault();
        setHealthOpen((open) => !open);
        return;
      }
      const result = applyShowcaseKey({
        key: event.key,
        pane,
        fallback,
        ppeEnabled: parsed.ppeEnabled,
        source,
        roomBCondition,
      });
      if (!result.handled) return;
      event.preventDefault();
      const resetKey = event.key === 'Escape' || event.key === '0' || event.key.toLowerCase() === 'r';
      if (resetKey) {
        clearShowcaseSession();
        setLampMessage(null);
        setHealthOpen(false);
        setResetNonce((value) => value + 1);
        const reset = presentationResetState();
        commit(reset.pane, reset.fallback, reset.source, reset.roomBCondition);
        return;
      }
      if (result.lampTest) {
        void adapter.lampTest().then((outcome) => {
          setLampMessage(
            outcome.connected
              ? `Lamp test ${outcome.sequence.join(' → ')}`
              : 'Light not connected. Presentation continues.',
          );
        });
        return;
      }
      commit(result.pane, result.fallback, result.source, result.roomBCondition);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [adapter, commit, fallback, pane, parsed.ppeEnabled, roomBCondition, source]);

  const showDashboard = visitorPane === 'booth' || visitorPane === 'forklift';
  const uptimeSec = Math.max(0, Math.round((Date.now() - startedAt) / 1000));

  return (
    <ShowcaseShell
      pane={pane}
      fallback={fallback}
      ppeEnabled={parsed.ppeEnabled}
      source={visitorSource}
      roomBCondition={roomBCondition}
      lampMessage={lampMessage}
    >
      {showDashboard ? (
        <ShowcaseNowPanes
          pane={visitorPane}
          fallback={fallback}
          booth={booth}
          forklift={forklift}
          liveNow={liveNow}
          boothScope={{
            deviceId: parsed.boothDeviceId,
            cameraId: parsed.boothCameraId,
            zoneId: parsed.boothZoneId,
          }}
          siteId={siteId}
          policyId={parsed.boothPolicyId}
          siteTimeZone={siteTimeZone}
          forkliftZone={{
            cameraId: parsed.forkliftCameraId,
            zoneId: forkliftZone.zoneId,
            polygon: forkliftZone.polygon,
          }}
          bundle={conditionData.bundle}
          status={conditionData.status}
          selectedWindowId={conditionData.selectedWindowId}
          onSelectWindow={conditionData.setSelectedWindowId}
          lampConnected={adapter.connected}
          proximityEvidence={proximityEvidence}
          raeEvidence={raeEvidence}
          boothPicture={boothPicture}
          onBoothPictureChange={setBoothPicture}
        />
      ) : null}
      {showDashboard && streamsOn && misStickyAnnotated && boothPicture === 'original' && booth.warmAnnotatedUrl ? (
        <Box
          component="img"
          data-testid="showcase-annotated-warm-consumer"
          src={booth.warmAnnotatedUrl}
          alt=""
          aria-hidden="true"
          sx={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        />
      ) : null}
      {pane === 'ppe' ? (
        <Box data-testid="showcase-ppe">
          <ShowcaseBadge label={SHOWCASE_LABEL_DEMO_DATASET} tone="demo" testId="showcase-ppe-dataset" />
          {fallback ? <ShowcaseBadge label={SHOWCASE_LABEL_FALLBACK} tone="fallback" testId="showcase-ppe-fallback" /> : null}
          <Typography sx={{ fontWeight: 800, fontSize: 22, mt: 1 }}>Visible PPE</Typography>
          <Typography sx={{ opacity: 0.8 }}>Optional demonstration card. Not live detections.</Typography>
          <Box
            component="img"
            src={SHOWCASE_PPE_STILL}
            alt="PPE demonstration still"
            sx={{ width: '100%', maxWidth: 640, mt: 2, borderRadius: 1 }}
          />
        </Box>
      ) : null}
      {healthOpen ? (
        <Box
          data-testid="showcase-health-drawer"
          sx={{ mt: 2, p: 1.5, border: '1px solid #24344a', borderRadius: 1, bgcolor: '#0f1b2d', fontSize: 13 }}
        >
          <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Operator health</Typography>
          <Typography data-testid="showcase-health-booth">Booth camera: {booth.status}</Typography>
          <Typography data-testid="showcase-health-forklift">Room B source: {forklift.status}. Processing not proven.</Typography>
          <Typography data-testid="showcase-health-phase">Condition state: {liveNow.phase}</Typography>
          <Typography data-testid="showcase-health-lifecycle">Lifecycle join: incident_id intersection</Typography>
          <Typography data-testid="showcase-health-buyer">Buyer-live: {liveNow.buyerLiveReadOk ? 'ok' : 'unavailable'}</Typography>
          <Typography data-testid="showcase-health-intersection">Intersection: {liveNow.matchingIncidentId || 'none'}</Typography>
          <Typography data-testid="showcase-health-zone">Qualifying membership: {liveNow.membershipReadOk ? `qualifying_count ${liveNow.occupancy}` : 'unread'}</Typography>
          <Typography data-testid="showcase-health-occupancy">Qualifying count: {liveNow.membershipReadOk ? `ok (${liveNow.occupancy})` : 'unavailable'}</Typography>
          <Typography data-testid="showcase-health-light">Lamp: {adapter.connected ? 'connected' : 'absent fail-open'}</Typography>
          <Typography data-testid="showcase-health-fallback">Fallback: {fallback ? 'on' : 'off'}</Typography>
          <Typography data-testid="showcase-health-fps">fps: not measured</Typography>
          <Typography data-testid="showcase-health-uptime">Session uptime: {uptimeSec}s</Typography>
          <Typography data-testid="showcase-health-keys">Keys: 1 Live Booth, 2 Room B, R reset, L light, F fallback</Typography>
        </Box>
      ) : null}
    </ShowcaseShell>
  );
}
